/* eslint-disable array-callback-return */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";

import {
  Button,
  Container,
  Dimmer,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Popup,
  Segment,
  Table,
  TextArea,
} from "semantic-ui-react";

import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

import { auth, db } from "../firebase/firebaseConfig";
import useObtenerIngreso from "../hooks/useObtenerIngreso";
import { UserContext } from "../context/UserContext";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import generarTraspasoRetornoHomeCare from "../funciones/generarTraspasosRetornoHomeCare";

/* =========================================================
   CONFIGURACIÓN
========================================================= */

const CATS_ADD = new Set([
  "CHECK",
  "MEDICION",
  "SEGURIDAD",
  "INSTRUMENTOS",
]);

const isEmpty = (v) => String(v ?? "").trim() === "";

const toMillisSafe = (x) =>
  x?.toMillis
    ? x.toMillis()
    : x?.toDate
      ? x.toDate().getTime()
      : +new Date(x ?? 0);

const formatFecha = (fecha) => {
  if (!fecha?.toDate) return "";
  return moment(fecha.toDate()).format("DD/MM/YYYY HH:mm");
};

/* =========================================================
   COMPONENTE
========================================================= */

const EjecutarPresupuestoSt = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { users } = useContext(UserContext);

  const user = auth.currentUser;

  const [ingreso] = useObtenerIngreso(id);

  const volver = useCallback(() => {
    navigate("/serviciotecnico/asignadostecnicos");
  }, [navigate]);

  /* =======================================================
     UI
  ======================================================= */

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const [openFinalConfirm, setOpenFinalConfirm] =
    useState(false);

  const [showCliente, setShowCliente] = useState(true);

  const [showPresuInfo, setShowPresuInfo] =
    useState(true);

  /* =======================================================
     BUSCADOR ITEMS
  ======================================================= */

  const [buscador, setBuscador] = useState("");

  const [showResultados, setShowResultados] =
    useState(false);

  const [verTodos, setVerTodos] = useState(false);

  const [hover, setHover] = useState(null);

  /* =======================================================
     PRESUPUESTO
  ======================================================= */

  const [idCabPre, setIdCabPre] = useState("");

  const [presuDetalle, setPresuDetalle] =
    useState([]);

  /*
   * Guardamos la cabecera COMPLETA del presupuesto.
   *
   * Esto permite conocer tipo_resolucion:
   *
   * REPARACION
   * BAJA_ST
   */
  const [presupuestoCab, setPresupuestoCab] =
    useState(null);

  /*
   * Determina si este presupuesto corresponde
   * a una resolución BAJA_ST.
   */
  const esBajaST =
    presupuestoCab?.tipo_resolucion === "BAJA_ST";

  const esCorrectivaContrato =
    ingreso?.origen_ingreso === "TRASPASO_ST_INTERNO" &&
    ingreso?.servicio === "PRESUPUESTO" &&
    !esBajaST;
  /* =======================================================
     CABECERA BITÁCORA PRESUPUESTO
  ======================================================= */

  const [cabPresuList, setCabPresuList] =
    useState([]);

  const cabBitIdRef = useRef("");

  /* =======================================================
     DETALLE BITÁCORA
  ======================================================= */

  const [bitacoraAll, setBitacoraAll] =
    useState([]);

  const [bitCheck, setBitCheck] =
    useState([]);

  const [bitMed, setBitMed] =
    useState([]);

  const [bitSeg, setBitSeg] =
    useState([]);

  const [bitInst, setBitInst] = useState([]);

  /* =======================================================
     MASTER ITEMS
  ======================================================= */

  const [itemsMaster, setItemsMaster] =
    useState([]);

  /* =======================================================
     OBSERVACIONES
  ======================================================= */

  const [obs, setObs] = useState("");

  /* =======================================================
     DATOS INGRESO
  ======================================================= */

  const datos = useMemo(() => {
    if (!ingreso) return null;

    return {
      folio: ingreso.folio ?? "",
      rut: ingreso.rut ?? "",
      entidad: ingreso.entidad ?? "",
      date: ingreso.date ?? null,
      telefono: ingreso.telefono ?? "",
      direccion: ingreso.direccion ?? "",
      correo: ingreso.correo ?? "",
      familia: ingreso.familia ?? "",
      tipo: ingreso.tipo ?? "",
      marca: ingreso.marca ?? "",
      modelo: ingreso.modelo ?? "",
      serie: ingreso.serie ?? "",
      servicio: ingreso.servicio ?? "",
    };
  }, [ingreso]);

  /* =======================================================
     HELPER FIRESTORE
  ======================================================= */

  const fetchDocs = useCallback(async (q) => {
    const snap = await getDocs(q);

    return snap.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    }));
  }, []);

  /* =======================================================
     CARGAR CABECERA PRESUPUESTO
  ======================================================= */

  const loadPresupuestoCab =
    useCallback(async () => {
      if (!users?.emp_id || !id) return;

      const qCab = query(
        collection(db, "presupuestoscab"),
        where("emp_id", "==", users.emp_id),
        where("id_cab_inst", "==", id)
      );

      const rows = await fetchDocs(qCab);

      if (rows.length > 0) {
        /*
         * Mantiene comportamiento actual.
         *
         * Más adelante podemos mejorar esto
         * para elegir explícitamente presupuesto
         * GENERADO / ACEPTADO si existieran varios.
         */
        setIdCabPre(rows[0].id);

        setPresupuestoCab(rows[0]);
      } else {
        setIdCabPre("");

        setPresupuestoCab(null);
      }
    }, [
      users?.emp_id,
      id,
      fetchDocs,
    ]);

  /* =======================================================
     CARGAR DETALLE PRESUPUESTO
  ======================================================= */

  const loadPresupuestoDetalle =
    useCallback(async () => {
      if (!users?.emp_id || !idCabPre) {
        setPresuDetalle([]);
        return;
      }

      const qDet = query(
        collection(db, "presupuestos"),
        where("emp_id", "==", users.emp_id),
        where("id_cab_pre", "==", idCabPre)
      );

      const rows = await fetchDocs(qDet);

      setPresuDetalle(rows);
    }, [
      users?.emp_id,
      idCabPre,
      fetchDocs,
    ]);

  /* =======================================================
     CARGAR CABECERA BITÁCORA
  ======================================================= */

  const loadCabBitacoraPresu =
    useCallback(async () => {
      if (!users?.emp_id || !id) return;

      const qCab = query(
        collection(db, "bitacoracabpresu"),
        where("emp_id", "==", users.emp_id),
        where("id_ingreso", "==", id)
      );

      const rows = await fetchDocs(qCab);

      setCabPresuList(rows);

      const pendientes = rows.filter(
        (x) => x.confirmado === false
      );

      const chosen =
        pendientes.length > 0
          ? pendientes
          : rows;

      if (chosen.length > 0) {
        const sorted = [...chosen].sort(
          (a, b) =>
            toMillisSafe(b?.fechamod) -
            toMillisSafe(a?.fechamod)
        );

        cabBitIdRef.current =
          sorted[0].id;

        setObs(
          sorted[0]?.observaciones ?? ""
        );
      } else {
        cabBitIdRef.current = "";

        setObs("");
      }
    }, [
      users?.emp_id,
      id,
      fetchDocs,
    ]);

  /* =======================================================
     CARGAR DETALLE BITÁCORA
  ======================================================= */

  const loadBitacoraDetalle =
    useCallback(async () => {
      if (
        !users?.emp_id ||
        !cabBitIdRef.current
      ) {
        setBitacoraAll([]);
        setBitCheck([]);
        setBitMed([]);
        setBitSeg([]);
        setBitInst([]);

        return;
      }

      const qAll = query(
        collection(db, "bitacorapresu"),
        where(
          "emp_id",
          "==",
          users.emp_id
        ),
        where(
          "id_cab_pre",
          "==",
          cabBitIdRef.current
        )
      );

      const rows =
        await fetchDocs(qAll);

      setBitacoraAll(rows);

      const ordenar = (categoria) =>
        rows
          .filter(
            (x) =>
              x.categoria === categoria
          )
          .sort(
            (a, b) =>
              toMillisSafe(a?.fechaadd) -
              toMillisSafe(b?.fechaadd)
          );

      const chk = ordenar("CHECK");
      const med = ordenar("MEDICION");
      const seg = ordenar("SEGURIDAD");
      const inst = ordenar("INSTRUMENTOS");

      setBitCheck(
        chk.map((x) => ({
          ...x,
          valor: x.valor ?? "",
        }))
      );

      setBitMed(
        med.map((x) => ({
          ...x,
          valor: x.valor ?? "",
        }))
      );

      setBitSeg(
        seg.map((x) => ({
          ...x,
          valor: x.valor ?? "",
        }))
      );

      setBitInst(
        inst.map((x) => ({
          ...x,
          valor: x.valor ?? "",
        }))
      );
    }, [
      users?.emp_id,
      fetchDocs,
    ]);

  /* =======================================================
     ITEMS MASTER
  ======================================================= */

  const loadItemsMaster =
    useCallback(async () => {
      if (!users?.emp_id) return;

      const qIt = query(
        collection(db, "items"),
        where(
          "emp_id",
          "==",
          users.emp_id
        )
      );

      const rows =
        await fetchDocs(qIt);

      setItemsMaster(rows);
    }, [
      users?.emp_id,
      fetchDocs,
    ]);

  /* =======================================================
     CARGA INICIAL
  ======================================================= */

  useEffect(() => {
    const run = async () => {
      if (!users?.emp_id || !id) return;

      setLoading(true);

      try {
        await loadPresupuestoCab();

        await loadCabBitacoraPresu();

        await loadItemsMaster();
      } catch (e) {
        Swal.fire(
          "Error cargando datos",
          String(e)
        );
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [
    users?.emp_id,
    id,
    loadPresupuestoCab,
    loadCabBitacoraPresu,
    loadItemsMaster,
  ]);

  useEffect(() => {
    loadPresupuestoDetalle();
  }, [
    loadPresupuestoDetalle,
  ]);

  useEffect(() => {
    loadBitacoraDetalle();
  }, [
    loadBitacoraDetalle,
  ]);

  /* =======================================================
     VISTAS PRESUPUESTO
  ======================================================= */

  /*
   * Para BAJA_ST mostramos también
   * el item IRREPARABLE como parte
   * de la condición técnica.
   */
  const falla = useMemo(() => {
    return presuDetalle
      .filter(
        (it) =>
          it.categoria === "FALLA" ||
          it.categoria === "IRREPARABLE"
      )
      .sort(
        (a, b) =>
          toMillisSafe(a?.fechamod) -
          toMillisSafe(b?.fechamod)
      );
  }, [
    presuDetalle,
  ]);

  const rys = useMemo(() => {
    return presuDetalle
      .filter(
        (it) =>
          it.categoria === "REPUESTO" ||
          it.categoria === "SERVICIO"
      )
      .sort(
        (a, b) =>
          toMillisSafe(a?.fechamod) -
          toMillisSafe(b?.fechamod)
      );
  }, [
    presuDetalle,
  ]);

  /* =======================================================
     SIGUIENTE
     INICIAR EJECUCIÓN
  ======================================================= */

  const onSiguiente = async () => {
    if (
      !users?.emp_id ||
      !datos?.folio
    ) {
      return;
    }

    if (!idCabPre) {
      Swal.fire(
        "No existe un presupuesto asociado al ingreso."
      );

      return;
    }

    setLoading(true);

    try {
      /*
       * Si existe una cabecera ya confirmada,
       * el trabajo ya fue ejecutado.
       */
      const confirmadas =
        cabPresuList.filter(
          (x) =>
            x.confirmado === true
        );

      if (
        confirmadas.length > 0
      ) {
        Swal.fire(
          esBajaST
            ? "La Baja ST de este ingreso ya fue ejecutada."
            : "Ya existe un presupuesto confirmado/trabajado para este ingreso."
        );

        return;
      }

      /*
       * Si existe ejecución pendiente,
       * simplemente la recuperamos.
       */
      const pendientes =
        cabPresuList.filter(
          (x) =>
            x.confirmado === false
        );

      if (
        pendientes.length > 0
      ) {
        await loadCabBitacoraPresu();

        await loadBitacoraDetalle();

        return;
      }

      const now = new Date();

      /*
       * Creamos cabecera de ejecución.
       *
       * Se agrega id_presupuesto para
       * dejar explícita la relación
       * con presupuestoscab.
       */
      const docRef = await addDoc(
        collection(
          db,
          "bitacoracabpresu"
        ),
        {
          fecha_mantencion: now,

          id_ingreso: id,

          folio_ingreso:
            datos.folio,

          /*
           * NUEVO:
           * presupuesto que estamos
           * ejecutando.
           */
          id_presupuesto:
            idCabPre,

          /*
           * NUEVO:
           * resolución del presupuesto.
           */
          tipo_resolucion:
            esBajaST
              ? "BAJA_ST"
              : "REPARACION",

          confirmado: false,

          observaciones: "",

          useradd:
            user?.email ?? "",

          usermod:
            user?.email ?? "",

          fechaadd: now,

          fechamod: now,

          emp_id:
            users.emp_id,
        }
      );

      cabBitIdRef.current =
        docRef.id;

      setObs("");

      /*
       * La orden entra en ejecución.
       */
      await updateDoc(
        doc(
          db,
          "ingresostcab",
          id
        ),
        {
          estado_servicio:
            "EJECUTANDO",

          usermod:
            user?.email ?? "",

          fechamod: now,
        }
      );

      /*
       * Para una Baja ST dejamos
       * además la resolución como
       * EN_EJECUCION.
       */
      if (esBajaST) {
        await updateDoc(
          doc(
            db,
            "presupuestoscab",
            idCabPre
          ),
          {
            estado_baja:
              "EN_EJECUCION",

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );
      }

      await loadCabBitacoraPresu();

      await loadBitacoraDetalle();

      Swal.fire({
        icon: "success",

        title: esBajaST
          ? "Ejecución de Baja ST iniciada"
          : "Puedes agregar items al presupuesto",

        timer: 1000,

        showConfirmButton:
          false,
      });
    } catch (e) {
      console.error(
        "Error iniciando ejecución:",
        e
      );

      Swal.fire(
        "Error",
        String(e)
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     ITEMS DISPONIBLES
  ======================================================= */

  const itemsAddBase =
    useMemo(() => {
      return itemsMaster
        .filter((it) =>
          CATS_ADD.has(
            it.categoria
          )
        )
        .sort(
          (a, b) =>
            toMillisSafe(
              a?.fechamod
            ) -
            toMillisSafe(
              b?.fechamod
            )
        );
    }, [
      itemsMaster,
    ]);

  const filtroItemBase =
    useMemo(() => {
      return itemsAddBase.filter(
        (it) =>
          !bitacoraAll.some(
            (p) =>
              p.item_id ===
              it.id
          )
      );
    }, [
      itemsAddBase,
      bitacoraAll,
    ]);

  const filtroItem =
    useMemo(() => {
      if (verTodos) {
        return filtroItemBase;
      }

      const term =
        buscador
          .toLowerCase()
          .trim();

      if (!term) return [];

      return filtroItemBase.filter(
        (it) => {
          const hay = [
            it.nombre,
            it.categoria,
            it.medida,
            String(
              it.inicial ?? ""
            ),
            String(
              it.final ?? ""
            ),
          ]
            .join(" ")
            .toLowerCase();

          return hay.includes(
            term
          );
        }
      );
    }, [
      verTodos,
      buscador,
      filtroItemBase,
    ]);

  /* =======================================================
     AGREGAR ITEM
  ======================================================= */

  const [
    botonDisabled,
    setBotonDisabled,
  ] = useState(false);

  const addItemToBitacora =
    async (itemId) => {
      if (!users?.emp_id) return;

      if (
        !cabBitIdRef.current
      ) {
        Swal.fire(
          "Primero presiona Siguiente para iniciar la ejecución del presupuesto."
        );

        return;
      }

      if (botonDisabled) {
        return;
      }

      setBotonDisabled(true);

      try {
        const it =
          itemsMaster.find(
            (x) =>
              x.id === itemId
          );

        if (!it) {
          return;
        }

        if (
          bitacoraAll.some(
            (p) =>
              p.item_id ===
              it.id
          )
        ) {
          Swal.fire(
            "Este item ya fue agregado."
          );

          return;
        }

        const now =
          new Date();

        await addDoc(
          collection(
            db,
            "bitacorapresu"
          ),
          {
            /*
             * Mantengo el nombre
             * existente para no
             * romper datos anteriores.
             *
             * Actualmente id_cab_pre
             * apunta a bitacoracabpresu.
             */
            id_cab_pre:
              cabBitIdRef.current,

            id_ingreso: id,

            // NUEVO:
            folio_ingreso:
              datos?.folio ?? "",
            /*
             * NUEVO:
             * referencia real
             * al presupuesto.
             */
            id_presupuesto:
              idCabPre,

            tipo_resolucion:
              esBajaST
                ? "BAJA_ST"
                : "REPARACION",

            item_id:
              it.id,

            item:
              it.nombre,

            categoria:
              it.categoria,

            medida:
              it.medida ?? "",

            inicial:
              Number(
                it.inicial ?? 0
              ),

            final:
              Number(
                it.final ?? 0
              ),

            valor: "",

            useradd:
              user?.email ?? "",

            usermod:
              user?.email ?? "",

            fechaadd: now,

            fechamod: now,

            emp_id:
              users.emp_id,
          }
        );

        await loadBitacoraDetalle();

        Swal.fire({
          icon: "success",

          title:
            "Item agregado",

          timer: 900,

          showConfirmButton:
            false,
        });
      } catch (e) {
        console.error(
          "Error agregando item:",
          e
        );

        Swal.fire(
          "Error agregando item",
          String(e)
        );
      } finally {
        setTimeout(
          () =>
            setBotonDisabled(
              false
            ),
          600
        );
      }
    };

  const handleSeleccionItem =
    (id_item) => {
      addItemToBitacora(
        id_item
      );

      setShowResultados(
        false
      );

      setBuscador("");

      setVerTodos(false);
    };

  /* =======================================================
     ELIMINAR ITEM
  ======================================================= */

  const askDelete =
    (idBitItem) => {
      setDeleteId(
        idBitItem
      );

      setOpenDelete(
        true
      );
    };

  const doDelete =
    async () => {
      if (!deleteId) return;

      setOpenDelete(false);

      setSaving(true);

      try {
        await deleteDoc(
          doc(
            db,
            "bitacorapresu",
            deleteId
          )
        );

        await loadBitacoraDetalle();

        Swal.fire({
          icon: "success",

          title:
            "Item eliminado",

          timer: 900,

          showConfirmButton:
            false,
        });
      } catch (e) {
        Swal.fire(
          "Error eliminando",
          String(e)
        );
      } finally {
        setSaving(false);

        setDeleteId("");
      }
    };

  /* =======================================================
     INPUTS MEDICIÓN / SEGURIDAD
  ======================================================= */

  const setMedValorById = (
    rowId,
    valor
  ) => {
    setBitMed((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
            ...r,
            valor,
          }
          : r
      )
    );
  };

  const setSegValorById = (
    rowId,
    valor
  ) => {
    setBitSeg((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
            ...r,
            valor,
          }
          : r
      )
    );
  };

  /* =======================================================
     GUARDAR AVANCE
  ======================================================= */

  const onGuardarAvance =
    async () => {
      if (
        !cabBitIdRef.current
      ) {
        Swal.fire(
          "Primero presiona Siguiente para iniciar la ejecución del presupuesto."
        );

        return false;
      }

      if (
        bitacoraAll.length ===
        0
      ) {
        Swal.fire(
          "Debes agregar al menos 1 item para guardar avance."
        );

        return false;
      }

      setSaving(true);

      try {
        const now =
          new Date();

        const batch =
          writeBatch(db);

        /*
         * Guardar mediciones.
         */
        bitMed.forEach(
          (r) => {
            batch.update(
              doc(
                db,
                "bitacorapresu",
                r.id
              ),
              {
                valor:
                  r.valor ?? "",

                usermod:
                  user?.email ??
                  "",

                fechamod:
                  now,
              }
            );
          }
        );

        /*
         * Guardar seguridad.
         */
        bitSeg.forEach(
          (r) => {
            batch.update(
              doc(
                db,
                "bitacorapresu",
                r.id
              ),
              {
                valor:
                  r.valor ?? "",

                usermod:
                  user?.email ??
                  "",

                fechamod:
                  now,
              }
            );
          }
        );

        /*
         * Observaciones cabecera.
         */
        batch.update(
          doc(
            db,
            "bitacoracabpresu",
            cabBitIdRef.current
          ),
          {
            observaciones:
              obs ?? "",

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );

        /*
         * En proceso.
         *
         * Se incorpora al mismo batch.
         */
        batch.update(
          doc(
            db,
            "ingresostcab",
            id
          ),
          {
            enproceso: "1",

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );

        await batch.commit();

        await loadCabBitacoraPresu();

        await loadBitacoraDetalle();

        Swal.fire({
          icon: "success",

          title:
            "Avance guardado",

          timer: 1000,

          showConfirmButton:
            false,
        });

        return true;
      } catch (e) {
        console.error(
          "Error guardando avance:",
          e
        );

        Swal.fire(
          "Error guardando avance",
          String(e)
        );

        return false;
      } finally {
        setSaving(false);
      }
    };

  /* =======================================================
     VALIDAR FINALIZACIÓN
  ======================================================= */

  const isComplete =
    useMemo(() => {
      if (
        bitacoraAll.length ===
        0
      ) {
        return false;
      }

      if (isEmpty(obs)) {
        return false;
      }

      return true;
    }, [
      bitacoraAll,
      obs,
    ]);

  const validateComplete =
    () => {
      if (
        bitacoraAll.length ===
        0
      ) {
        return esBajaST
          ? "Debes agregar al menos 1 evidencia técnica para la Baja ST."
          : "Debes agregar al menos 1 item al presupuesto.";
      }

      if (isEmpty(obs)) {
        return "Campo Observaciones no puede estar vacío.";
      }

      return null;
    };

  /* =======================================================
     CLICK FINALIZAR
  ======================================================= */

  const onFinalizarClick =
    () => {
      const err =
        validateComplete();

      if (err) {
        return Swal.fire(
          err
        );
      }

      setOpenFinalConfirm(
        true
      );
    };

  /* =======================================================
     FINALIZAR
  ======================================================= */

  const onFinalizarConfirmado =
    async () => {
      setOpenFinalConfirm(
        false
      );

      /*
       * Validamos nuevamente
       * antes de escribir.
       */
      const err =
        validateComplete();

      if (err) {
        return Swal.fire(
          err
        );
      }

      setSaving(true);

      try {
        const now =
          new Date();

        if (
          !cabBitIdRef.current
        ) {
          throw new Error(
            "No existe cabecera de ejecución."
          );
        }

        /*
         * =================================================
         * IMPORTANTE
         *
         * Aquí guardamos nuevamente valores y observaciones
         * en el mismo batch de finalización.
         *
         * Evitamos llamar onGuardarAvance(),
         * porque esa función maneja su propio try/catch
         * y antes una falla podía permitir continuar
         * igualmente con la finalización.
         * =================================================
         */

        const batch =
          writeBatch(db);

        bitMed.forEach(
          (r) => {
            batch.update(
              doc(
                db,
                "bitacorapresu",
                r.id
              ),
              {
                valor:
                  r.valor ?? "",

                usermod:
                  user?.email ??
                  "",

                fechamod:
                  now,
              }
            );
          }
        );

        bitSeg.forEach(
          (r) => {
            batch.update(
              doc(
                db,
                "bitacorapresu",
                r.id
              ),
              {
                valor:
                  r.valor ?? "",

                usermod:
                  user?.email ??
                  "",

                fechamod:
                  now,
              }
            );
          }
        );

        /*
         * Cerrar cabecera ejecución.
         */
        batch.update(
          doc(
            db,
            "bitacoracabpresu",
            cabBitIdRef.current
          ),
          {
            confirmado: true,

            observaciones:
              obs ?? "",

            tipo_resolucion:
              esBajaST
                ? "BAJA_ST"
                : "REPARACION",

            usermod:
              user?.email ?? "",

            fechamod:
              now,
          }
        );

        /*
         * La ejecución técnica termina.
         *
         * Se respeta la regla del ST:
         *
         * estado = REALIZADO
         * estado_servicio = REALIZADO
         *
         * Posteriormente, cuando exista entrega
         * física, estado pasará a ENTREGADO pero
         * estado_servicio seguirá REALIZADO.
         */
        batch.update(
          doc(
            db,
            "ingresostcab",
            id
          ),
          {
            enproceso: "0",

            estado:
              "REALIZADO",

            estado_servicio:
              "REALIZADO",

            fecha_out: now,

            usermod:
              user?.email ?? "",

            fechamod: now,
          }
        );

        /*
   * =========================================================
   * BAJA ST
   *
   * Al finalizar:
   *
   * 1. presupuestoscab queda REALIZADA
   * 2. se crea bajaEquipos
   * 3. la serie queda bloqueada para futuros ingresos ST
   * =========================================================
   */
        if (
          esBajaST &&
          idCabPre
        ) {
          /*
           * -------------------------------------------------------
           * VALIDAR SERIE
           * -------------------------------------------------------
           */
          const serieNormalizada =
            String(
              datos?.serie ?? ""
            )
              .trim()
              .toUpperCase();

          if (!serieNormalizada) {
            throw new Error(
              "El equipo no posee un número de serie válido."
            );
          }

          /*
           * -------------------------------------------------------
           * IDENTIFICAR CONDICIÓN IRREPARABLE
           * -------------------------------------------------------
           */
          const irreparable =
            presuDetalle.find(
              (item) =>
                item.categoria ===
                "IRREPARABLE"
            );

          if (!irreparable) {
            throw new Error(
              "No se encontró el item IRREPARABLE asociado a esta Baja ST."
            );
          }

          /*
           * -------------------------------------------------------
           * VALIDAR QUE NO EXISTA YA UNA BAJA ST ACTIVA
           * PARA ESTA MISMA SERIE
           * -------------------------------------------------------
           */
          const qBajaExistente =
            query(
              collection(
                db,
                "bajaEquipos"
              ),
              where(
                "emp_id",
                "==",
                users.emp_id
              ),
              where(
                "serie",
                "==",
                serieNormalizada
              )
            );

          const snapBajaExistente =
            await getDocs(
              qBajaExistente
            );

          const existeBajaActiva =
            snapBajaExistente.docs.some(
              (d) => {
                const baja =
                  d.data();

                return (
                  baja.tipo_baja ===
                  "BAJA_ST" &&
                  baja.activo ===
                  true &&
                  baja.bloquea_ingreso_st ===
                  true
                );
              }
            );

          if (existeBajaActiva) {
            throw new Error(
              `El equipo con serie ${serieNormalizada} ya posee una Baja ST activa.`
            );
          }

          /*
           * -------------------------------------------------------
           * ACTUALIZAR RESOLUCIÓN DEL PRESUPUESTO
           * -------------------------------------------------------
           */
          batch.update(
            doc(
              db,
              "presupuestoscab",
              idCabPre
            ),
            {
              tipo_resolucion:
                "BAJA_ST",

              baja_st:
                true,

              estado_baja:
                "REALIZADA",

              fecha_baja:
                now,

              usermod:
                user?.email ?? "",

              fechamod:
                now,
            }
          );

          /*
           * -------------------------------------------------------
           * CREAR REGISTRO PERMANENTE BAJA EQUIPO
           * -------------------------------------------------------
           */
          const bajaEquipoRef =
            doc(
              collection(
                db,
                "bajaEquipos"
              )
            );

          batch.set(
            bajaEquipoRef,
            {
              /*
               * RELACIONES
               */
              folio_oe:
                datos?.folio ?? "",

              id_cab_inst:
                id,

              id_presupuesto:
                idCabPre,

              id_bitacoracabpresu:
                cabBitIdRef.current,

              /*
               * EQUIPO
               */
              familia:
                datos?.familia ?? "",

              tipo:
                datos?.tipo ?? "",

              marca:
                datos?.marca ?? "",

              modelo:
                datos?.modelo ?? "",

              serie:
                serieNormalizada,

              /*
               * RESOLUCIÓN TÉCNICA
               */
              irreparable:
                irreparable?.item ??
                "",

              observaciones:
                isEmpty(obs)
                  ? "Sin Observaciones"
                  : obs,

              /*
               * ESTADO BAJA ST
               */
              tipo_baja:
                "BAJA_ST",

              estado:
                "ACTIVA",

              activo:
                true,

              bloquea_ingreso_st:
                true,

              /*
               * AUDITORÍA
               */
              fechabaja:
                now,

              fechaadd:
                now,

              fechamod:
                now,

              useradd:
                user?.email ?? "",

              usermod:
                user?.email ?? "",

              emp_id:
                users.emp_id,
            }
          );
        }

        await batch.commit();
        // =====================================================
        // RETORNO AUTOMÁTICO A HOMECARE
        // =====================================================
        if (esCorrectivaContrato) {
          const ordenRetorno = {
            ...ingreso,
            id,
            estado: "REALIZADO",
            estado_servicio: "REALIZADO",
            fecha_out: now,
          };

          const resultadoRetorno =
            await generarTraspasoRetornoHomeCare({
              orden: ordenRetorno,
              userEmail: user?.email || "",
              linkNotificacion: "/transacciones/traspasos",
            });

          console.log(
            "Resultado retorno HomeCare:",
            resultadoRetorno
          );
        }
        Swal.fire({
          icon: "success",

          title: esBajaST
            ? "Ejecución de Baja ST realizada con éxito"
            : esCorrectivaContrato
              ? "Reparación finalizada y retorno a HomeCare generado"
              : "Ejecución del Presupuesto realizada con éxito",

          showConfirmButton: true,
        }).then(() => {
          volver();
        });

      } catch (e) {
        console.error(
          "Error al finalizar:",
          e
        );

        Swal.fire(
          "Error al finalizar",
          e?.message ||
          String(e)
        );
      } finally {
        setSaving(false);
      }
    };

  /* =======================================================
     CARGANDO INGRESO
  ======================================================= */

  if (!datos) {
    return (
      <Container>
        <Segment>
          <Message info icon>
            <Icon name="info circle" />

            <Message.Content>
              <Message.Header>
                Cargando ingreso...
              </Message.Header>
            </Message.Content>
          </Message>
        </Segment>
      </Container>
    );
  }

  const hasBitacora =
    !!cabBitIdRef.current;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Container
      style={{
        paddingBottom: 24,
      }}
    >
      {/* =================================================
          HEADER
      ================================================= */}


      <Segment
        style={{
          background: "#ffffff",
          border: "1px solid #e5e9ef",
          borderRadius: 9,
          boxShadow: "0 2px 8px rgba(23,43,77,0.04)",
          padding: "20px 24px",
          marginBottom: 18,
        }}
      >
        <Header
          as="h2"
          style={{
            margin: 0,
            color: "#172b4d",
            fontSize: 23,
            fontWeight: 700,
          }}
        >
          <Icon
            name={
              esBajaST
                ? "trash alternate"
                : "clipboard check"
            }
            style={{
              color: esBajaST ? "#db2828" : "#0d9da5",
            }}
          />

          <Header.Content>
            {esBajaST
              ? "Ejecutar Baja ST"
              : "Ejecutar Presupuesto"}

            <Header.Subheader
              style={{
                color: "#66758c",
                fontSize: 13,
                fontWeight: 400,
                marginTop: 6,
              }}
            >
              {esBajaST
                ? "Registra la evidencia técnica que respalda la Baja de Servicio Técnico"
                : "Revisa, ejecuta y genera documentación del presupuesto asignado"}
            </Header.Subheader>
          </Header.Content>
        </Header>
      </Segment>


      {/* =================================================
          LOADER
      ================================================= */}

      <Dimmer
        active={
          loading ||
          saving
        }
        inverted
      >
        <Loader>
          {saving
            ? "Guardando..."
            : "Cargando..."}
        </Loader>
      </Dimmer>

      {/* =================================================
          AVISO BAJA ST
      ================================================= */}

      {esBajaST && (
        <Message
          warning
          icon
        >
          <Icon
            name="warning sign"
          />

          <Message.Content>
            <Message.Header>
              Resolución técnica: Baja ST
            </Message.Header>

            Este proceso registrará la evidencia técnica que
            respalda la resolución de Baja de Servicio Técnico
            para el equipo.
          </Message.Content>
        </Message>
      )}

      {esCorrectivaContrato && (
        <Message info icon>
          <Icon name="shield alternate" />

          <Message.Content>
            <Message.Header>
              Correctiva cubierta por contrato
            </Message.Header>

            La mano de obra y los servicios asociados a esta reparación
            están cubiertos por el contrato de mantención. Los repuestos
            corresponden a los ítems aprobados en el presupuesto.
          </Message.Content>
        </Message>
      )}

      {/* =================================================
          CLIENTE / EQUIPO
      ================================================= */}

      <Segment raised>
        <Grid
          verticalAlign="middle"
        >
          <Grid.Row columns={2}>
            <Grid.Column>
              <Header as="h3">
                <Icon name="user" />

                <Header.Content>
                  Información Cliente / Equipo
                </Header.Content>
              </Header>
            </Grid.Column>

            <Grid.Column
              textAlign="right"
            >
              <Button
                basic
                icon
                onClick={() =>
                  setShowCliente(
                    (s) => !s
                  )
                }
              >
                <Icon
                  name={
                    showCliente
                      ? "angle up"
                      : "angle down"
                  }
                />
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {showCliente && (
          <>
            <Table
              celled
              compact
              size="small"
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    Folio
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Rut
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Nombre
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Fecha
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Teléfono
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Dirección
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Email
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                <Table.Row>
                  <Table.Cell>
                    {datos.folio}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.rut}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.entidad}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.date
                      ? formatFecha(
                        datos.date
                      )
                      : "-"}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.telefono}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.direccion}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.correo}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>

            <Table
              celled
              compact
              size="small"
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    Familia
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Tipo
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Marca
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Modelo
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Serie
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Servicio
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                <Table.Row>
                  <Table.Cell>
                    {datos.familia}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.tipo}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.marca}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.modelo}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.serie}
                  </Table.Cell>

                  <Table.Cell>
                    {datos.servicio}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              {/* VOLVER AL LISTADO */}
              <Button
                basic
                color="grey"
                onClick={volver}
                disabled={loading || saving}
              >
                <Icon name="arrow left" />
                Volver
              </Button>

              {/* INICIAR EJECUCIÓN */}
              <Button
                color={esBajaST ? "red" : "teal"}
                onClick={onSiguiente}
                disabled={
                  loading ||
                  saving ||
                  hasBitacora
                }
              >
                <Icon name="play" />

                {hasBitacora
                  ? "Ejecución iniciada"
                  : esBajaST
                    ? "Iniciar ejecución Baja ST"
                    : "Siguiente"}
              </Button>
            </div>
          </>
        )}
      </Segment>

      {/* =================================================
          INFORMACIÓN PRESUPUESTO
      ================================================= */}

      <Segment raised>
        <Grid
          verticalAlign="middle"
        >
          <Grid.Row columns={2}>
            <Grid.Column>
              <Header as="h3">
                <Icon name="file alternate outline" />

                <Header.Content>
                  Información Presupuesto
                </Header.Content>
              </Header>
            </Grid.Column>

            <Grid.Column
              textAlign="right"
            >
              <Button
                basic
                icon
                onClick={() =>
                  setShowPresuInfo(
                    (s) => !s
                  )
                }
              >
                <Icon
                  name={
                    showPresuInfo
                      ? "angle up"
                      : "angle down"
                  }
                />
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {showPresuInfo && (
          <>
            <Header as="h4">
              {esBajaST
                ? "Diagnóstico / Condición Técnica"
                : "Diagnóstico y Falla"}
            </Header>

            <Table
              celled
              compact
              size="small"
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    width={1}
                  >
                    N°
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Item
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    width={3}
                  >
                    Categoría
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {falla.map(
                  (it, idx) => (
                    <Table.Row
                      key={
                        it.id ??
                        idx
                      }
                    >
                      <Table.Cell>
                        {idx + 1}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          whiteSpace:
                            "normal",
                        }}
                      >
                        {it.item}
                      </Table.Cell>

                      <Table.Cell>
                        {it.categoria}
                      </Table.Cell>
                    </Table.Row>
                  )
                )}

                {falla.length ===
                  0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={3}
                        textAlign="center"
                      >
                        Sin fallas registradas
                      </Table.Cell>
                    </Table.Row>
                  )}
              </Table.Body>
            </Table>

            <Header as="h4">
              {esBajaST
                ? "Resolución / Servicio"
                : "Presupuesto"}
            </Header>

            <Table
              celled
              compact
              size="small"
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    width={1}
                  >
                    N°
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Item
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    width={3}
                  >
                    Categoría
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    width={3}
                  >
                    Precio
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {rys.map(
                  (it, idx) => (
                    <Table.Row
                      key={
                        it.id ??
                        idx
                      }
                    >
                      <Table.Cell>
                        {idx + 1}
                      </Table.Cell>

                      <Table.Cell
                        style={{
                          whiteSpace:
                            "normal",
                        }}
                      >
                        {it.item}
                      </Table.Cell>

                      <Table.Cell>
                        {it.categoria}
                      </Table.Cell>

                      <Table.Cell>
                        {typeof it.price === "number"
                          ? `$${it.price.toLocaleString()}.-`
                          : "-"}

                        {esCorrectivaContrato &&
                          it.categoria === "SERVICIO" && (
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: "0.82em",
                                fontWeight: 600,
                                color: "#00a89d",
                              }}
                            >
                              <Icon name="check circle" />
                              Cubierto por contrato
                            </div>
                          )}
                      </Table.Cell>
                    </Table.Row>
                  )
                )}

                {rys.length ===
                  0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={4}
                        textAlign="center"
                      >
                        Sin items en presupuesto
                      </Table.Cell>
                    </Table.Row>
                  )}
              </Table.Body>
            </Table>
          </>
        )}
      </Segment>

      {/* =================================================
          EJECUCIÓN
      ================================================= */}

      <Segment
        raised
        color={
          hasBitacora
            ? esBajaST
              ? "red"
              : "teal"
            : undefined
        }
      >
        <Header as="h3">
          <Icon name="tasks" />

          <Header.Content>
            {esBajaST
              ? "Evidencia Técnica de Baja ST"
              : "Items ejecutados (Bitácora)"}
          </Header.Content>
        </Header>

        {!hasBitacora && (
          <Message info icon>
            <Icon name="info circle" />

            <Message.Content>
              <Message.Header>
                Presiona “
                {esBajaST
                  ? "Iniciar ejecución Baja ST"
                  : "Siguiente"}
                ” para iniciar
              </Message.Header>

              {esBajaST
                ? "Luego podrás registrar CHECK, MEDICIONES, SEGURIDAD y observaciones que respaldarán técnicamente la Baja ST."
                : "Luego podrás agregar items, guardar avances y finalizar."}
            </Message.Content>
          </Message>
        )}

        {hasBitacora && (
          <>
            {/* ===========================================
                BUSCADOR
            =========================================== */}

            <div
              style={{
                position:
                  "relative",
              }}
            >
              <Input
                fluid
                icon="search"
                placeholder={
                  esBajaST
                    ? "Buscar y agregar evidencia técnica"
                    : "Buscar y agregar items (Enter: ver todos / cerrar)"
                }
                value={
                  buscador
                }
                onChange={(
                  e
                ) => {
                  const value =
                    e.target
                      .value;

                  setBuscador(
                    value
                  );

                  if (
                    value.trim() ===
                    ""
                  ) {
                    setShowResultados(
                      false
                    );

                    setVerTodos(
                      false
                    );
                  } else {
                    setVerTodos(
                      false
                    );

                    setShowResultados(
                      true
                    );
                  }
                }}
                onKeyDown={(
                  e
                ) => {
                  if (
                    e.key ===
                    "Enter"
                  ) {
                    e.preventDefault();

                    if (
                      !verTodos &&
                      !showResultados
                    ) {
                      setVerTodos(
                        true
                      );

                      setShowResultados(
                        true
                      );

                      return;
                    }

                    if (
                      verTodos ||
                      showResultados
                    ) {
                      setVerTodos(
                        false
                      );

                      setShowResultados(
                        false
                      );

                      setBuscador(
                        ""
                      );
                    }
                  }
                }}
              />

              {showResultados && (
                <div
                  style={{
                    position:
                      "absolute",

                    top: "100%",

                    left: 0,

                    right: 0,

                    maxHeight:
                      260,

                    overflowY:
                      "auto",

                    border:
                      "1px solid #ddd",

                    borderRadius:
                      6,

                    background:
                      "#fff",

                    boxShadow:
                      "0 6px 16px rgba(0,0,0,0.15)",

                    marginTop:
                      6,

                    zIndex:
                      1000,
                  }}
                >
                  <Table
                    compact
                    celled
                    singleLine
                  >
                    <Table.Header
                      style={{
                        position:
                          "sticky",

                        top: 0,

                        zIndex: 2,

                        background:
                          "#f8f8f8",
                      }}
                    >
                      <Table.Row>
                        <Table.HeaderCell
                          style={{
                            width:
                              60,
                          }}
                        >
                          N°
                        </Table.HeaderCell>

                        <Table.HeaderCell>
                          Item
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          style={{
                            width:
                              140,
                          }}
                        >
                          Categoría
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          style={{
                            width:
                              90,

                            textAlign:
                              "center",
                          }}
                        >
                          Agregar
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {filtroItem.map(
                        (
                          it,
                          idx
                        ) => (
                          <Table.Row
                            key={
                              it.id
                            }
                            onMouseEnter={() =>
                              setHover(
                                it.id
                              )
                            }
                            onMouseLeave={() =>
                              setHover(
                                null
                              )
                            }
                            style={{
                              cursor:
                                "pointer",

                              backgroundColor:
                                hover ===
                                  it.id
                                  ? "#F2F2F2"
                                  : "white",
                            }}
                          >
                            <Table.Cell>
                              {idx +
                                1}
                            </Table.Cell>

                            <Table.Cell>
                              {
                                it.nombre
                              }
                            </Table.Cell>

                            <Table.Cell>
                              {
                                it.categoria
                              }
                            </Table.Cell>

                            <Table.Cell
                              textAlign="center"
                            >
                              <Popup
                                content="Agregar item"
                                inverted
                                trigger={
                                  <Icon
                                    name="add circle"
                                    color={
                                      botonDisabled
                                        ? "grey"
                                        : "teal"
                                    }
                                    size="large"
                                    style={{
                                      cursor:
                                        "pointer",
                                    }}
                                    onClick={() =>
                                      handleSeleccionItem(
                                        it.id
                                      )
                                    }
                                  />
                                }
                              />
                            </Table.Cell>
                          </Table.Row>
                        )
                      )}

                      {filtroItem.length ===
                        0 && (
                          <Table.Row>
                            <Table.Cell
                              colSpan={
                                4
                              }
                              textAlign="center"
                            >
                              No hay
                              coincidencias
                              {buscador
                                ? ` para "${buscador}"`
                                : ""}
                              .
                            </Table.Cell>
                          </Table.Row>
                        )}
                    </Table.Body>
                  </Table>
                </div>
              )}
            </div>

            <Divider />

            {/* ===========================================
                SIN ITEMS
            =========================================== */}

            {bitacoraAll.length ===
              0 && (
                <Message
                  info
                  icon
                >
                  <Icon name="info circle" />

                  <Message.Content>
                    <Message.Header>
                      Aún no hay items agregados
                    </Message.Header>

                    {esBajaST
                      ? "Usa el buscador para agregar la evidencia técnica que respaldará la Baja ST."
                      : "Usa el buscador para agregar al menos 1 item."}
                  </Message.Content>
                </Message>
              )}

            {/* ===========================================
                CHECK
            =========================================== */}

            {bitCheck.length >
              0 && (
                <>
                  <Header as="h4">
                    CHECK
                  </Header>

                  <Table
                    celled
                    compact
                    size="small"
                  >
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell
                          width={
                            1
                          }
                        >
                          N°
                        </Table.HeaderCell>

                        <Table.HeaderCell>
                          Item
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          width={
                            3
                          }
                        >
                          Categoría
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          width={
                            2
                          }
                        >
                          Eliminar
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {bitCheck.map(
                        (
                          it,
                          idx
                        ) => (
                          <Table.Row
                            key={
                              it.id
                            }
                          >
                            <Table.Cell>
                              {idx +
                                1}
                            </Table.Cell>

                            <Table.Cell
                              style={{
                                whiteSpace:
                                  "normal",
                              }}
                            >
                              {
                                it.item
                              }
                            </Table.Cell>

                            <Table.Cell>
                              {
                                it.categoria
                              }
                            </Table.Cell>

                            <Table.Cell>
                              <Button
                                basic
                                color="red"
                                size="tiny"
                                onClick={() =>
                                  askDelete(
                                    it.id
                                  )
                                }
                              >
                                <Icon name="trash" />
                                Eliminar
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        )
                      )}
                    </Table.Body>
                  </Table>
                </>
              )}

            {/* ===========================================
                MEDICIÓN
            =========================================== */}

            {bitMed.length >
              0 && (
                <>
                  <Header as="h4">
                    MEDICIÓN
                  </Header>

                  <Table
                    celled
                    compact
                    size="small"
                  >
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell
                          width={
                            1
                          }
                        >
                          N°
                        </Table.HeaderCell>

                        <Table.HeaderCell>
                          Item
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          width={
                            3
                          }
                        >
                          Valor
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          width={
                            3
                          }
                        >
                          Rango
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          width={
                            2
                          }
                        >
                          Medida
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          width={
                            2
                          }
                        >
                          Eliminar
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {bitMed.map(
                        (
                          it,
                          idx
                        ) => (
                          <Table.Row
                            key={
                              it.id
                            }
                          >
                            <Table.Cell>
                              {idx +
                                1}
                            </Table.Cell>

                            <Table.Cell
                              style={{
                                whiteSpace:
                                  "normal",
                              }}
                            >
                              {
                                it.item
                              }
                            </Table.Cell>

                            <Table.Cell>
                              <Form.Input
                                type="number"
                                value={
                                  it.valor
                                }
                                onChange={(
                                  e,
                                  d
                                ) =>
                                  setMedValorById(
                                    it.id,
                                    d.value
                                  )
                                }
                                placeholder="0"
                                size="small"
                              />
                            </Table.Cell>

                            <Table.Cell>
                              {Number(
                                it.inicial
                              )}{" "}
                              -{" "}
                              {Number(
                                it.final
                              )}
                            </Table.Cell>

                            <Table.Cell>
                              {
                                it.medida
                              }
                            </Table.Cell>

                            <Table.Cell>
                              <Button
                                basic
                                color="red"
                                size="tiny"
                                onClick={() =>
                                  askDelete(
                                    it.id
                                  )
                                }
                              >
                                <Icon name="trash" />

                                Eliminar
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        )
                      )}
                    </Table.Body>
                  </Table>
                </>
              )}

            {/* ===========================================
                SEGURIDAD
            =========================================== */}

            {bitSeg.length >
              0 && (
                <>
                  <Header as="h4">
                    SEGURIDAD
                  </Header>

                  <Table
                    celled
                    compact
                    size="small"
                  >
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell
                          width={
                            1
                          }
                        >
                          N°
                        </Table.HeaderCell>

                        <Table.HeaderCell>
                          Item
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          width={
                            4
                          }
                        >
                          Valor
                        </Table.HeaderCell>

                        <Table.HeaderCell
                          width={
                            2
                          }
                        >
                          Eliminar
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {bitSeg.map(
                        (
                          it,
                          idx
                        ) => (
                          <Table.Row
                            key={
                              it.id
                            }
                          >
                            <Table.Cell>
                              {idx +
                                1}
                            </Table.Cell>

                            <Table.Cell
                              style={{
                                whiteSpace:
                                  "normal",
                              }}
                            >
                              {
                                it.item
                              }
                            </Table.Cell>

                            <Table.Cell>
                              <Form.Input
                                type="text"
                                value={
                                  it.valor
                                }
                                onChange={(
                                  e,
                                  d
                                ) =>
                                  setSegValorById(
                                    it.id,
                                    d.value
                                  )
                                }
                                placeholder="Ingrese valor"
                                size="small"
                              />
                            </Table.Cell>

                            <Table.Cell>
                              <Button
                                basic
                                color="red"
                                size="tiny"
                                onClick={() =>
                                  askDelete(
                                    it.id
                                  )
                                }
                              >
                                <Icon name="trash" />

                                Eliminar
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        )
                      )}
                    </Table.Body>
                  </Table>
                </>
              )}

            {/* ===========================================
                INSTRUMENTOS
            =========================================== */}

            {bitInst.length > 0 && (
              <>
                <Header as="h4">
                  INSTRUMENTOS
                </Header>

                <Table
                  celled
                  compact
                  size="small"
                >
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={1}>
                        N°
                      </Table.HeaderCell>

                      <Table.HeaderCell>
                        Instrumento
                      </Table.HeaderCell>

                      <Table.HeaderCell width={3}>
                        Categoría
                      </Table.HeaderCell>

                      <Table.HeaderCell width={2}>
                        Eliminar
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {bitInst.map(
                      (it, idx) => (
                        <Table.Row key={it.id}>
                          <Table.Cell>
                            {idx + 1}
                          </Table.Cell>

                          <Table.Cell
                            style={{
                              whiteSpace: "normal",
                            }}
                          >
                            {it.item}
                          </Table.Cell>

                          <Table.Cell>
                            {it.categoria}
                          </Table.Cell>

                          <Table.Cell>
                            <Button
                              basic
                              color="red"
                              size="tiny"
                              onClick={() =>
                                askDelete(it.id)
                              }
                            >
                              <Icon name="trash" />
                              Eliminar
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      )
                    )}
                  </Table.Body>
                </Table>
              </>
            )}


            {/* ===========================================
                OBSERVACIONES
            =========================================== */}

            <Segment>
              <Header as="h4">
                <Icon name="sticky note" />

                <Header.Content>
                  {esBajaST
                    ? "Observaciones / Fundamento Técnico"
                    : "Observaciones"}
                </Header.Content>
              </Header>

              <TextArea
                placeholder={
                  esBajaST
                    ? "Ingrese el fundamento técnico y observaciones que respaldan la Baja ST"
                    : "Ingrese observación o detalles adicionales"
                }
                value={obs}
                onChange={(e) =>
                  setObs(
                    e.target
                      .value
                  )
                }
                style={{
                  minHeight:
                    90,

                  width:
                    "100%",
                }}
              />
            </Segment>

            {/* ===========================================
                BOTONES
            =========================================== */}

            <Grid>
              <Grid.Row
                columns={2}
              >
                <Grid.Column>
                  <Button
                    basic
                    onClick={
                      volver
                    }
                  >
                    <Icon name="arrow left" />

                    Volver
                  </Button>
                </Grid.Column>

                <Grid.Column
                  textAlign="right"
                >
                  <Button
                    color="teal"
                    onClick={
                      onGuardarAvance
                    }
                    disabled={
                      saving ||
                      loading ||
                      bitacoraAll.length ===
                      0
                    }
                    title={
                      bitacoraAll.length ===
                        0
                        ? "Agrega al menos 1 item"
                        : "Guardar avance"
                    }
                  >
                    <Icon name="save" />

                    Guardar avance
                  </Button>

                  <Button
                    color={
                      esBajaST
                        ? "red"
                        : "green"
                    }
                    onClick={
                      onFinalizarClick
                    }
                    disabled={
                      !isComplete ||
                      saving ||
                      loading
                    }
                    style={{
                      marginLeft:
                        8,
                    }}
                    title={
                      !isComplete
                        ? "Requiere: 1 item + Observaciones"
                        : esBajaST
                          ? "Finalizar Baja ST"
                          : "Finalizar"
                    }
                  >
                    <Icon
                      name={
                        esBajaST
                          ? "trash alternate"
                          : "flag checkered"
                      }
                    />

                    {esBajaST
                      ? "Finalizar Baja ST"
                      : "Finalizar"}
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            {!isComplete && (
              <Message
                info
                icon
                style={{
                  marginTop:
                    12,
                }}
              >
                <Icon name="info circle" />

                <Message.Content>
                  <Message.Header>
                    Para finalizar
                  </Message.Header>

                  Debes agregar{" "}
                  <b>
                    al menos 1
                    item
                  </b>{" "}
                  y completar{" "}
                  <b>
                    Observaciones
                  </b>
                  .
                </Message.Content>
              </Message>
            )}
          </>
        )}
      </Segment>

      {/* =================================================
          MODAL ELIMINAR
      ================================================= */}

      <Modal
        size="small"
        open={openDelete}
        onClose={() =>
          setOpenDelete(
            false
          )
        }
      >
        <Modal.Header>
          Eliminar item
        </Modal.Header>

        <Modal.Content>
          <Message warning icon>
            <Icon name="warning circle" />

            <Message.Content>
              <Message.Header>
                ¿Eliminar este item?
              </Message.Header>

              Esta acción no
              se puede
              deshacer.
            </Message.Content>
          </Message>
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={() =>
              setOpenDelete(
                false
              )
            }
          >
            <Icon name="close" />

            Cancelar
          </Button>

          <Button
            color="red"
            onClick={
              doDelete
            }
          >
            <Icon name="trash" />

            Eliminar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* =================================================
          MODAL FINALIZAR
      ================================================= */}

      <Modal
        size="small"
        open={
          openFinalConfirm
        }
        onClose={() =>
          setOpenFinalConfirm(
            false
          )
        }
      >
        <Modal.Header>
          {esBajaST
            ? "Confirmar Baja ST"
            : "Confirmar finalización"}
        </Modal.Header>

        <Modal.Content>
          <Message warning icon>
            <Icon name="warning circle" />

            <Message.Content>
              <Message.Header>
                {esBajaST
                  ? "¿Seguro que deseas finalizar la ejecución de la Baja ST?"
                  : "¿Seguro que deseas finalizar la ejecución del presupuesto?"}
              </Message.Header>

              {esBajaST ? (
                <>
                  Se cerrará
                  la ejecución
                  técnica y el
                  ingreso quedará{" "}
                  <b>
                    REALIZADO
                  </b>
                  .
                </>
              ) : (
                <>
                  Se marcará el
                  ingreso como{" "}
                  <b>
                    REALIZADO
                  </b>{" "}
                  y se
                  confirmará la
                  bitácora.
                </>
              )}
            </Message.Content>
          </Message>
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={() =>
              setOpenFinalConfirm(
                false
              )
            }
          >
            <Icon name="close" />

            Cancelar
          </Button>

          <Button
            color={
              esBajaST
                ? "red"
                : "green"
            }
            onClick={() => {
              const err =
                validateComplete();

              if (err) {
                return Swal.fire(
                  err
                );
              }

              onFinalizarConfirmado();
            }}
          >
            <Icon name="check" />

            {esBajaST
              ? " Sí, finalizar Baja ST"
              : " Sí, finalizar"}
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default EjecutarPresupuestoSt;